import React, { useState } from 'react';
import { styled } from '@mui/material';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { useAuth } from 'contexts/auth';
import { useTour } from 'contexts/tour';
import { Box, Button, Drawer, Icon, IconButton, Logo, Stack, Text } from 'shell/components';

const EnsightfulLogo = () => {
    const location = useLocation();
    const { auth } = useAuth();

    let homePath = '/';
    if (location.pathname !== '/') {
        homePath = auth.role === 'instructor' ? '/overview' : '/dashboard';
    }

    return (
        <StyledLink aria-label={'Ensightful'} to={homePath}>
            <Logo />
        </StyledLink>
    );
};

const Mobile = ({ navOptions, dropdownOptions, unreadMessages, logout, changeAccount }) => {
    const history = useHistory();
    const location = useLocation();
    const { handleReset } = useTour();

    const [open, setOpen] = useState(false);

    navOptions = [
        ...(navOptions || []).filter((navOption) => navOption.key !== 'more'),
        ...((navOptions || []).find((navOption) => navOption.key === 'more')?.options || []).map(({ key, value }) => {
            return { key, text: value, link: `/${key}` };
        }),
    ];

    const handleClick = (to) => {
        setOpen(false);
        history.push(to);
    };

    const handleClickOption = ({ key }) => {
        if (key === 'profile') {
            setOpen(false);
            return history.push('/profile');
        }
        if (key === 'tour') {
            setOpen(false);
            return handleReset();
        }
        if (key === 'switch-course') {
            setOpen(false);
            return changeAccount();
        }
        if (key === 'course-dashboard') {
            setOpen(false);
            return history.push('/');
        }
        if (key === 'logout') {
            return logout();
        }
    };

    const renderNavOptions = () => {
        return navOptions.map((navOption) => {
            return (
                <Box key={navOption.key} sx={{ position: 'relative' }}>
                    <Button
                        sx={{ minWidth: 'unset' }}
                        color={location.pathname === navOption.link ? 'primary' : 'neutral'}
                        size={'large'}
                        startIcon={navOption.icon ? <Icon kind={navOption.icon} /> : null}
                        variant={'tertiary'}
                        onClick={() => handleClick(navOption.link)}
                    >
                        {navOption.text}
                    </Button>
                    {navOption.key === 'message' && unreadMessages && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '0.25rem',
                                left: '3.75rem',
                                borderRadius: '50%',
                                width: '0.625rem',
                                height: '0.625rem',
                                background: (theme) => theme.palette.ensightful.primary[500],
                            }}
                        />
                    )}
                </Box>
            );
        });
    };

    const activeOption = navOptions.find(({ link }) => location.pathname.startsWith(link));

    const getPageHeader = () => {
        if (location.pathname.includes('/profile')) return 'Profile';
        return activeOption?.text;
    };

    return (
        <CustomStack alignItems={'center'} direction={'row'}>
            <EnsightfulLogo />
            <Text variant={'p1'} weight={'semibold'}>
                {getPageHeader()}
            </Text>
            <IconButton
                aria-label={'navigation menu'}
                data-cy={'nav-hamburger-menu-button'}
                sx={{ mr: -2 }}
                kind={open ? 'exit' : 'hamburger'}
                size={'medium'}
                variant={'square'}
                onClick={() => setOpen((current) => !current)}
            />
            <Drawer device={'mobile'} open={open} onClose={() => setOpen(false)}>
                <Stack alignItems={'flex-start'} spacing={2}>
                    {renderNavOptions()}
                </Stack>
                <Stack
                    sx={{ position: 'absolute', bottom: (theme) => theme.spacing(5) }}
                    alignItems={'flex-start'}
                    spacing={2}
                >
                    {dropdownOptions
                        .filter((option) => !['user', 'divider'].includes(option.key))
                        .map((option) => (
                            <Button
                                key={option.key}
                                color={'neutral'}
                                size={'small'}
                                variant={'tertiary'}
                                onClick={() => handleClickOption(option)}
                            >
                                {option.value}
                            </Button>
                        ))}
                </Stack>
            </Drawer>
        </CustomStack>
    );
};

const StyledLink = styled(Link)(({ theme }) => {
    return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(2),
        height: '100%',
        '&:focus-visible': {
            borderRadius: '6px',
            outline: `${theme.palette.ensightful.secondary[100]} solid 3px`,
        },
    };
});

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        justifyContent: 'space-between',
        width: '100%',
        height: '100%',
        padding: theme.spacing(0, 5),
    };
});

export default Mobile;
